/* ──────────────────────────────────────────────────────────────────────
 * BookingDensity — "The Season Ahead"
 *
 * A magazine-spread quarterly calendar: three full months displayed side
 * by side as a luxury hotel reservation board. NOT a heatmap, NOT a
 * widget — a 520-px showpiece that fills the card.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │ HEADER  ── label · serif title · subtitle ─── │ nav · view · today │
 *   │                                           │ 4 large stat tiles    │
 *   ├────────────────────────────────────────────────────────────────┤
 *   │   May 2026     │     June 2026     │     July 2026             │
 *   │   M T W T F S S│   M T W T F S S   │   M T W T F S S           │
 *   │   ┌─┬─┬─┐...   │   ┌─┬─┬─┐...     │   ┌─┬─┬─┐...               │
 *   │   │ │ │ │      │   │ │ │ │        │   │ │ │ │                  │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Cells: 6 × 7 grid per month, ~48 px tall, flexible width (1fr).
 * Cell skins:
 *   empty           → cream
 *   1 event         → soft champagne wash + 1 chip
 *   2-3 events      → deeper champagne + stacked chips + count dot
 *   4+ events       → rich gold gradient + glow + "FULL" badge
 *   today           → gold ring + breathing dot + serif gold date
 *   weekend         → +1% warm tint + hairline gold top border
 *   out-of-month    → 40% opacity, no chips
 *
 * Tooltip on hover shows date + event list + value + "View day details →".
 * Click an in-month cell → onDateClick(iso).
 *
 * Data:
 *   Caller passes `byDate: Record<iso, count>`. When that's missing or
 *   sparse the component generates seeded mock data — Wedding /
 *   Corporate / Birthday / Anniversary events with realistic values and
 *   a Saturday-in-June peak. The mock IS the deliverable for the
 *   dashboard spec.
 * ──────────────────────────────────────────────────────────────────── */

import React, { useMemo, useState, useCallback } from "react";
import { Box, Typography, Stack, Tooltip, IconButton, Button, ToggleButton, ToggleButtonGroup } from "@mui/material";
import ChevronLeftIcon  from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  startOfMonth, endOfMonth, addMonths, subMonths, addDays, format,
  isSameDay, isSameMonth, isToday as isTodayDate, isWeekend,
} from "date-fns";

/* ── Palette ─────────────────────────────────────────────────────────── */
const CREAM        = "#FAF7F2";
const CHAMP_1      = "#FAEFD6";
const CHAMP_2      = "#F0DFA8";
const CHAMP_3      = "#E8D9B0";
const CHAMP_4      = "#D4BC7A";
const CHAMP_5      = "#B89C5A";
const GOLD         = "#C9A961";
const GOLD_DEEP    = "#A88B47";
const INK          = "#2C2A26";
const MUTED        = "#8B8680";
const HAIR         = "rgba(44,42,38,0.08)";
const HAIR_GOLD    = "rgba(201,169,97,0.35)";
const SAGE         = "#7A9B76";

const FONT_SERIF = `'Fraunces', 'GT Sectra', 'Tiempos Headline', Georgia, serif`;
const FONT_MONO  = `'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace`;

/* One day of the calendar. After the mock-data sweep the only signal
 * we have from byDate is "N events on this day", so the model is
 * just `count` — no per-event titles/types/guests/values to
 * fabricate. */
interface DayData {
  iso:   string;
  date:  Date;
  count: number;
}

/* ── Small primitives ────────────────────────────────────────────────── */

const StatTile: React.FC<{ value: string; label: string; first?: boolean }> = ({ value, label, first }) => (
  <Stack direction="row" alignItems="stretch" gap={1.6}>
    {!first && <Box sx={{ width: 1, bgcolor: HAIR, alignSelf: "stretch" }} />}
    <Box sx={{ px: 0.4 }}>
      <Typography sx={{
        fontFamily: FONT_SERIF, fontSize: 26, fontWeight: 400,
        color: INK, lineHeight: 1, letterSpacing: "-0.02em",
        fontVariantNumeric: "tabular-nums",
      }}>
        {value}
      </Typography>
      <Typography sx={{
        fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700,
        color: MUTED, letterSpacing: "0.18em", textTransform: "uppercase",
        mt: 0.6,
      }}>
        {label}
      </Typography>
    </Box>
  </Stack>
);

/* EventChip was removed in the mock-data sweep — it relied on
 * per-event `type` which isn't carried by the byDate count map. The
 * cell's tint + count badge convey volume without misleading labels. */

/* ── DateCell ────────────────────────────────────────────────────────── */
const DateCell: React.FC<{
  day: DayData;
  inMonth: boolean;
  isToday: boolean;
  weekend: boolean;
  onClick?: (iso: string) => void;
  /** Animation delay in ms (column-month stagger + row stagger). */
  delay: number;
}> = ({ day, inMonth, isToday, weekend, onClick, delay }) => {
  const eventCount = day.count;
  const isPeak     = eventCount >= 4;

  /* ── Pick skin based on event load ───────────────────────────────── */
  let bg: string = CREAM;
  let dateColor = "#A09B92";
  let dateFont  = "sans";
  if (inMonth) {
    if (eventCount === 0)      { bg = CREAM;   dateColor = "#A09B92"; dateFont = "sans";  }
    else if (eventCount === 1) { bg = CHAMP_1; dateColor = INK;        dateFont = "serif"; }
    else if (eventCount <= 3)  { bg = CHAMP_2; dateColor = INK;        dateFont = "serif"; }
    else                       { bg = `linear-gradient(135deg, ${CHAMP_4} 0%, ${CHAMP_5} 100%)`; dateColor = "#fff"; dateFont = "serif"; }
  }
  if (weekend && inMonth && eventCount === 0) bg = "#F7F2E8"; // 1% warmer

  /* Single, deterministic boxShadow ladder. The TS compiler treats
   * later object-spread keys as overriding earlier ones, so we cannot
   * set boxShadow in the base block AND in the `isToday` spread —
   * compute the final value once and apply it. */
  const boxShadow: string = isToday
    ? `0 4px 14px rgba(201,169,97,0.30), inset 0 0 0 1px rgba(255,255,255,0.5)`
    : isPeak
      ? `0 0 14px 0 rgba(201,169,97,0.35), 0 1px 2px rgba(176,134,52,0.18)`
      : eventCount === 0 && inMonth
        ? `inset 0 0 0 1px rgba(44,42,38,0.04)`
        : "none";

  /* ── Tooltip content — date + honest event count.
       The per-event "Wedding · Patel · 170 guests · $80K" rows were
       removed in the mock sweep — byDate only carries a count, not
       per-event titles / guests / values, so those lines were
       fabricated. Tooltip now stays factual: date + N events. */
  const tooltipBody = inMonth ? (
    <Box sx={{ p: 0.6 }}>
      <Typography sx={{
        fontFamily: FONT_SERIF, fontSize: 14, fontWeight: 500,
        color: "#fff", lineHeight: 1.2,
      }}>
        {format(day.date, "EEEE, MMMM d")}
      </Typography>
      {eventCount > 0 && (
        <Typography sx={{
          fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 700,
          color: "rgba(255,255,255,0.7)", letterSpacing: "0.14em",
          textTransform: "uppercase", mt: 0.4,
        }}>
          {eventCount} event{eventCount === 1 ? "" : "s"} booked
        </Typography>
      )}
      <Typography sx={{
        fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700,
        color: GOLD, letterSpacing: "0.14em", textTransform: "uppercase",
        mt: 1, pt: 0.8, borderTop: "1px solid rgba(255,255,255,0.12)",
        cursor: "pointer",
      }}>
        View day details →
      </Typography>
    </Box>
  ) : "";

  return (
    <Tooltip
      placement="top"
      arrow
      enterDelay={140}
      title={tooltipBody}
      disableHoverListener={!inMonth}
    >
      <Box
        component="button"
        onClick={() => inMonth && onClick?.(day.iso)}
        aria-label={`${format(day.date, "EEEE MMMM d")}${eventCount ? `, ${eventCount} events` : ", no events"}`}
        sx={{
          all: "unset",
          position: "relative",
          height: 48,
          minWidth: 0,
          borderRadius: "5px",
          background: bg,
          opacity: inMonth ? 1 : 0.32,
          cursor: inMonth ? "pointer" : "default",
          overflow: "hidden",
          boxShadow,
          ...(weekend && inMonth ? {
            borderTop: `1px solid ${HAIR_GOLD}`,
          } : {}),
          ...(isToday ? {
            outline: `2px solid ${GOLD}`,
            outlineOffset: "1px",
            zIndex: 4,
            animation: `bd-cell-fade-in 360ms cubic-bezier(0.16,1,0.3,1) ${delay}ms both, bd-today-breathe 2400ms ease-in-out ${delay + 1200}ms infinite`,
          } : isPeak ? {
            animation: `bd-cell-fade-in 360ms cubic-bezier(0.16,1,0.3,1) ${delay}ms both, bd-peak-glow 4000ms ease-in-out ${delay + 1500}ms infinite`,
          } : {
            opacity: inMonth ? 0 : 0.32,
            animation: inMonth
              ? `bd-cell-fade-in 360ms cubic-bezier(0.16,1,0.3,1) ${delay}ms forwards`
              : `bd-cell-fade-out-month 360ms cubic-bezier(0.16,1,0.3,1) ${delay}ms forwards`,
          }),
          transition: "transform 160ms ease, filter 160ms ease",
          "&:hover": inMonth ? {
            transform: "scale(1.02)",
            filter: "brightness(1.04)",
            zIndex: 5,
          } : {},
        }}
      >
        {/* Date number — top-left */}
        <Box sx={{
          position: "absolute",
          top: 4,
          left: 6,
          fontFamily: dateFont === "serif" ? FONT_SERIF : FONT_MONO,
          fontSize: dateFont === "serif" ? 14 : 11,
          fontWeight: dateFont === "serif" ? 500 : 600,
          color: isToday ? GOLD_DEEP : dateColor,
          lineHeight: 1,
          letterSpacing: dateFont === "serif" ? "-0.01em" : "0.04em",
        }}>
          {format(day.date, "d")}
        </Box>

        {/* Today: breathing dot — top-right */}
        {isToday && (
          <Box sx={{
            position: "absolute",
            top: 4, right: 4,
            width: 6, height: 6,
            borderRadius: "50%",
            bgcolor: GOLD,
            animation: `bd-pulse-dot 1800ms ease-in-out ${delay + 1400}ms infinite`,
          }} />
        )}

        {/* Count dot for 2-3 event days — top-right */}
        {!isToday && eventCount >= 2 && eventCount <= 3 && inMonth && (
          <Box sx={{
            position: "absolute",
            top: 5, right: 5,
            fontFamily: FONT_MONO,
            fontSize: 8,
            fontWeight: 800,
            color: GOLD_DEEP,
            letterSpacing: "0.02em",
            bgcolor: "rgba(255,255,255,0.65)",
            borderRadius: "2px",
            px: 0.35,
            lineHeight: 1.3,
          }}>
            {eventCount}
          </Box>
        )}

        {/* Peak "FULL" badge — top-right */}
        {isPeak && inMonth && (
          <Box sx={{
            position: "absolute",
            top: 4, right: 4,
            fontFamily: FONT_MONO,
            fontSize: 7.5,
            fontWeight: 800,
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.18)",
            letterSpacing: "0.10em",
            borderRadius: "2px",
            px: 0.5, py: 0.1,
            lineHeight: 1.3,
          }}>
            FULL · {eventCount}
          </Box>
        )}

        {/* Per-cell event chips were dropped in the mock-data sweep —
            byDate only carries a count, never a per-event type/title,
            so the chip's "WEDDING" / "CORPORATE" label would be
            fabricated. Cell tint + the count badge top-right already
            communicate event volume honestly. */}
      </Box>
    </Tooltip>
  );
};

/* ── MonthBlock ──────────────────────────────────────────────────────── */
function buildMonthGrid(anchor: Date): Date[] {
  const first  = startOfMonth(anchor);
  const offset = (first.getDay() + 6) % 7;   // days back to Monday
  const start  = addDays(first, -offset);
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) days.push(addDays(start, i));
  return days;
}

const MonthBlock: React.FC<{
  anchor: Date;
  today: Date;
  byIso: Record<string, DayData>;
  columnDelay: number;
  onDateClick?: (iso: string) => void;
}> = ({ anchor, today, byIso, columnDelay, onDateClick }) => {
  const grid = useMemo(() => buildMonthGrid(anchor), [anchor]);

  /* Month-level summary — events + revenue across the in-month days only. */
  const summary = useMemo(() => {
    let events = 0;
    for (const d of grid) {
      if (!isSameMonth(d, anchor)) continue;
      const k = format(d, "yyyy-MM-dd");
      const day = byIso[k];
      if (!day) continue;
      events += day.count;
    }
    return { events };
  }, [grid, anchor, byIso]);

  return (
    <Box
      sx={{
        flex: 1, minWidth: 0,
        px: 2.4,
        opacity: 0,
        animation: `bd-month-fade-in 420ms cubic-bezier(0.16, 1, 0.3, 1) ${columnDelay}ms forwards`,
      }}
    >
      {/* Month header */}
      <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mb: 1.4 }}>
        <Box>
          <Typography sx={{
            fontFamily: FONT_SERIF, fontSize: 22, fontWeight: 400,
            color: INK, lineHeight: 1, letterSpacing: "-0.02em",
          }}>
            {format(anchor, "MMMM")}
          </Typography>
          <Typography sx={{
            fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 700,
            color: MUTED, letterSpacing: "0.20em", textTransform: "uppercase",
            mt: 0.4,
          }}>
            {format(anchor, "yyyy")}
          </Typography>
        </Box>
        <Typography sx={{
          fontFamily: FONT_MONO, fontSize: 10, fontWeight: 600,
          color: MUTED, letterSpacing: "0.06em",
          fontVariantNumeric: "tabular-nums",
        }}>
          {summary.events} events
        </Typography>
      </Stack>

      {/* Weekday header row */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        columnGap: "2px",
        mb: 0.8,
      }}>
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => {
          const wknd = i >= 5;
          return (
            <Typography
              key={i}
              sx={{
                fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700,
                color: wknd ? GOLD_DEEP : MUTED,
                letterSpacing: "0.18em", textTransform: "uppercase",
                textAlign: "center", lineHeight: 1.2,
              }}
            >
              {d}
            </Typography>
          );
        })}
      </Box>

      {/* 6 × 7 date grid */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gridTemplateRows: "repeat(6, 48px)",
        columnGap: "2px",
        rowGap: "2px",
      }}>
        {grid.map((d, idx) => {
          const iso     = format(d, "yyyy-MM-dd");
          const day     = byIso[iso] ?? { iso, date: d, count: 0 };
          const inMonth = isSameMonth(d, anchor);
          const today_  = isSameDay(d, today);
          const wknd    = isWeekend(d);
          const row     = Math.floor(idx / 7);
          const delay   = columnDelay + row * 30 + Math.floor((idx % 7) * 6);
          return (
            <DateCell
              key={iso}
              day={day}
              inMonth={inMonth}
              isToday={today_}
              weekend={wknd}
              onClick={onDateClick}
              delay={delay}
            />
          );
        })}
      </Box>
    </Box>
  );
};

/* ── Main ────────────────────────────────────────────────────────────── */

interface Props {
  /** Forward window of confirmed events keyed by ISO date. Rendered
   *  as-is; days not present in the map render as empty. There is no
   *  longer a mock fallback — the calendar shows what's in the data. */
  byDate?: Record<string, number>;
  /** Forward days to consider for stats. Defaults 90. */
  days?: number;
  onDateClick?: (isoDate: string) => void;
}

export default function BookingDensity({ byDate, days = 90, onDateClick }: Props) {
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  // Navigation: anchor is the first of the three-month window.
  const [anchor, setAnchor] = useState<Date>(() => startOfMonth(today));
  const [view, setView]     = useState<"month" | "quarter" | "year">("quarter");

  /* Build a 110-day dataset (10 days before anchor → 100 after) from
   * real byDate. Days not present in byDate render as empty cells. No
   * mock fallback — the calendar shows what's in the data. */
  const data = useMemo<DayData[]>(() => {
    const windowStart = addDays(anchor, -10);
    const windowDays  = 110;
    const out: DayData[] = [];
    for (let i = 0; i < windowDays; i++) {
      const d   = addDays(windowStart, i);
      const iso = format(d, "yyyy-MM-dd");
      out.push({ iso, date: d, count: byDate?.[iso] ?? 0 });
    }
    return out;
  }, [byDate, anchor]);

  const byIso = useMemo<Record<string, DayData>>(() => {
    const out: Record<string, DayData> = {};
    for (const d of data) out[d.iso] = d;
    return out;
  }, [data]);

  /* ── Top-line stats — computed over the next `days` forward window
   *    from TODAY (not the navigation anchor), so the header always
   *    reflects "the season ahead" regardless of nav.
   *
   *    `totalValue` was removed in the mock sweep — byDate only carries
   *    a count, so summing fake `event.value` produced fake revenue. */
  const stats = useMemo(() => {
    const forward = data.filter((d) => d.date >= today && d.date <= addDays(today, days - 1));
    let totalEvents = 0;
    let openDays    = 0;
    let peak: DayData | null = null;
    for (const d of forward) {
      const n = d.count;
      totalEvents += n;
      if (n === 0) openDays += 1;
      if (!peak || n > peak.count) peak = d;
    }
    return { totalEvents, openDays, peak };
  }, [data, today, days]);

  const months = [anchor, addMonths(anchor, 1), addMonths(anchor, 2)];

  const handlePrev  = useCallback(() => setAnchor((a) => subMonths(a, 3)), []);
  const handleNext  = useCallback(() => setAnchor((a) => addMonths(a, 3)), []);
  const handleToday = useCallback(() => setAnchor(startOfMonth(today)), [today]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: 520,
        bgcolor: "#FFFDF9",
      }}
    >
      {/* ── Keyframes — inlined so the component is self-contained ── */}
      <Box
        component="style"
        sx={{ display: "none" } as any}
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes bd-month-fade-in {
              from { opacity: 0; transform: translateY(8px); }
              to   { opacity: 1; transform: translateY(0);   }
            }
            @keyframes bd-cell-fade-in {
              from { opacity: 0; transform: translateY(4px); }
              to   { opacity: 1; transform: translateY(0);   }
            }
            @keyframes bd-cell-fade-out-month {
              from { opacity: 0; }
              to   { opacity: 0.32; }
            }
            @keyframes bd-chip-pop {
              from { opacity: 0; transform: scale(0.8); }
              to   { opacity: 1; transform: scale(1);   }
            }
            @keyframes bd-today-breathe {
              0%,100% { box-shadow: 0 4px 14px rgba(201,169,97,0.30), inset 0 0 0 1px rgba(255,255,255,0.5); }
              50%     { box-shadow: 0 6px 22px rgba(201,169,97,0.55), inset 0 0 0 1px rgba(255,255,255,0.7); }
            }
            @keyframes bd-pulse-dot {
              0%,100% { transform: scale(1);   opacity: 1; }
              50%     { transform: scale(1.4); opacity: 0.55; }
            }
            @keyframes bd-peak-glow {
              0%,100% { box-shadow: 0 0 12px rgba(201,169,97,0.30), 0 1px 2px rgba(176,134,52,0.18); }
              50%     { box-shadow: 0 0 22px rgba(201,169,97,0.55), 0 1px 4px rgba(176,134,52,0.28); }
            }
          `,
        }}
      />

      {/* ──────────────────────────── HEADER ──────────────────────── */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{
          px: 3.2,
          pt: 3,
          pb: 2.6,
          borderBottom: `1px solid ${HAIR}`,
          gap: 3,
        }}
      >
        {/* Left — editorial title */}
        <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
          <Typography sx={{
            fontFamily: FONT_MONO, fontSize: 10, fontWeight: 700,
            color: MUTED, letterSpacing: "0.22em", textTransform: "uppercase",
            mb: 0.8,
          }}>
            Booking Density · Next {days} Days
          </Typography>
          <Typography sx={{
            fontFamily: FONT_SERIF, fontSize: 36, fontWeight: 400,
            color: INK, lineHeight: 1, letterSpacing: "-0.025em",
          }}>
            The season ahead
          </Typography>
          <Typography sx={{
            fontFamily: FONT_SERIF, fontSize: 13.5, fontStyle: "italic",
            color: MUTED, mt: 0.7, letterSpacing: "0.005em",
          }}>
            Every event, every confirmation, every celebration.
          </Typography>
        </Box>

        {/* Right — nav + stat tiles */}
        <Stack gap={1.8} sx={{ flexShrink: 0 }}>
          {/* Navigation controls */}
          <Stack direction="row" alignItems="center" gap={1.2} justifyContent="flex-end">
            <Tooltip title="Previous 3 months" arrow>
              <IconButton size="small" onClick={handlePrev}
                sx={{
                  border: `1px solid ${HAIR}`,
                  borderRadius: "8px",
                  color: INK,
                  "&:hover": { borderColor: GOLD, color: GOLD_DEEP },
                }}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <ToggleButtonGroup
              size="small"
              value={view}
              exclusive
              onChange={(_, v) => v && setView(v)}
              sx={{
                "& .MuiToggleButton-root": {
                  fontFamily: FONT_MONO,
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: MUTED,
                  borderColor: HAIR,
                  px: 1.2,
                  py: 0.4,
                  "&.Mui-selected": {
                    bgcolor: "rgba(201,169,97,0.14)",
                    color: GOLD_DEEP,
                    borderColor: HAIR_GOLD,
                    "&:hover": { bgcolor: "rgba(201,169,97,0.20)" },
                  },
                },
              }}
            >
              <ToggleButton value="month">Month</ToggleButton>
              <ToggleButton value="quarter">Quarter</ToggleButton>
              <ToggleButton value="year">Year</ToggleButton>
            </ToggleButtonGroup>

            <Button
              size="small"
              onClick={handleToday}
              sx={{
                fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 800,
                letterSpacing: "0.18em", textTransform: "uppercase",
                color: "#fff", bgcolor: GOLD,
                borderRadius: "8px",
                px: 1.4, py: 0.5,
                "&:hover": { bgcolor: GOLD_DEEP },
              }}
            >
              Today
            </Button>

            <Tooltip title="Next 3 months" arrow>
              <IconButton size="small" onClick={handleNext}
                sx={{
                  border: `1px solid ${HAIR}`,
                  borderRadius: "8px",
                  color: INK,
                  "&:hover": { borderColor: GOLD, color: GOLD_DEEP },
                }}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Stat tiles — Projected Revenue was removed in the mock
              sweep (byDate carries no per-event $ values). Remaining 3
              tiles compute from the real booking count. */}
          <Stack direction="row" alignItems="stretch" gap={1.6}>
            <StatTile
              first
              value={String(stats.totalEvents)}
              label={`Events booked`}
            />
            <StatTile
              value={stats.peak ? format(stats.peak.date, "EEE MMM d") : "—"}
              label="Peak day"
            />
            <StatTile
              value={String(stats.openDays)}
              label="Open days"
            />
          </Stack>
        </Stack>
      </Stack>

      {/* ────────────────────────── CALENDAR ──────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          pt: 2.8,
          pb: 3.2,
          minHeight: 360,
        }}
      >
        {months.map((m, idx) => (
          <React.Fragment key={m.toISOString()}>
            <MonthBlock
              anchor={m}
              today={today}
              byIso={byIso}
              columnDelay={idx * 200}
              onDateClick={onDateClick}
            />
            {idx < months.length - 1 && (
              <Box sx={{
                width: "1px",
                alignSelf: "stretch",
                mx: 0.4,
                bgcolor: HAIR,
              }} />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}
